/* What each employee was paid, by month.
 *
 * Employee.baseSalary is one figure with no time dimension, so payroll read
 * the CURRENT salary for every month -- raising someone's pay silently
 * recomputed every closed month. salary_history holds a row per employee per
 * effective month, and payroll picks the latest row at or before the month
 * being computed.
 *
 * A month with no applicable row pays NOTHING. That is deliberate: an
 * employee who joined in December was not on the books in September, and
 * September must not show a cost for them. */
import { api } from "./api";

const BASE = "/salary-history";

export const salaryHistoryApi = {
  /** Every row, all employees. Small table -- one row per revision. */
  async listAll() {
    const rows = await api.get(BASE);
    return (rows || []).map((r) => ({
      employeeId: r.employeeId,
      effectiveFrom: r.effectiveFrom,          // "YYYY-MM"
      baseSalary: Number(r.baseSalary) || 0,
      note: r.note || "",
    }));
  },

  async upsert(employeeId, effectiveFrom, baseSalary, note) {
    return api.patch(`${BASE}/${encodeURIComponent(employeeId)}`, {
      effectiveFrom, baseSalary, note: note || undefined,
    });
  },
};

/* The rate in force for `monthLabel` ("September 2026").
 *
 * Returns null when history has not loaded yet, so the caller can hold the
 * previous figure rather than flashing zero; returns 0 when history HAS
 * loaded and no row applies, which is a real answer -- that person was not
 * being paid that month. */
const MONTHS = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];

export function monthLabelToYM(label) {
  const [mn, yr] = String(label || "").split(" ");
  const i = MONTHS.indexOf(mn);
  return i < 0 || !yr ? null : `${yr}-${String(i + 1).padStart(2, "0")}`;
}

export function salaryFor(rows, employeeId, monthLabel) {
  if (!Array.isArray(rows)) return null;      // not loaded
  const ym = monthLabelToYM(monthLabel);
  if (!ym) return 0;
  const applicable = rows
    .filter((r) => r.employeeId === employeeId && r.effectiveFrom <= ym)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
  return applicable.length ? applicable[0].baseSalary : 0;
}
